import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ArrowLeft, Send, Users, Lock, Unlock, UserMinus, X, Paperclip, Smile, Video, Radio, MoreVertical, Flag, Edit2, Trash2, Check, Gamepad2 } from 'lucide-react';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import { LiveModeSelector, BroadcastView, ConferenceView } from '@/components/live';
import { ReportModal } from '@/components/report';
import { BlackBoxGame } from '@/components/game';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let socket;

// Generate a unique vibrant color for each user based on their ID
const getUserColor = (userId) => {
  const colors = [
    '#FF6B6B', // Coral Red
    '#4ECDC4', // Teal
    '#FFE66D', // Golden Yellow
    '#95E1D3', // Mint
    '#F38181', // Salmon
    '#AA96DA', // Lavender
    '#81C784', // Sage Green
    '#FFB347', // Peach Orange
    '#87CEEB', // Sky Blue
    '#DDA0DD', // Plum
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const ChatRoom = ({ user }) => {
  const { popupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isHistory = location.state?.isHistory;
  const historySession = location.state?.session;

  const [popup, setPopup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showBranchList, setShowBranchList] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [saveSession, setSaveSession] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLiveModeSelector, setShowLiveModeSelector] = useState(false);
  const [liveSession, setLiveSession] = useState(null);
  const [isInLiveSession, setIsInLiveSession] = useState(false);
  const [showSaveRecordingDialog, setShowSaveRecordingDialog] = useState(false);
  const [pendingS3Key, setPendingS3Key] = useState(null);
  const [isSavingRecording, setIsSavingRecording] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [reportModal, setReportModal] = useState({ open: false, message: null });
  const [deleteMessageDialog, setDeleteMessageDialog] = useState({ open: false, message: null });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showGame, setShowGame] = useState(false);
  const [gameInvite, setGameInvite] = useState(null); // {inviter, player_count}
  const [activeGamePlayers, setActiveGamePlayers] = useState(0); // live count of players in game
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const hasJoined = useRef(false);

  useEffect(() => {
    if (isHistory) {
      // Load history session
      setMessages(historySession.messages);
      // Create a popup object from the session data
      setPopup({
        id: historySession.popup_id,
        name: historySession.popup_name,
        admin_id: historySession.admin_id,
        admin_username: historySession.participants[0]?.username || 'Unknown',
        branches: historySession.participants.slice(1),
        is_personal: false,
        is_active: false
      });
      return;
    }

    // Initialize socket
    socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      console.log('Socket connected to chat room');
      socket.emit('join_room', { popup_id: popupId });
    });

    socket.on('new_message', (message) => {
      setMessages(prev => [...prev, message]);
    });

    socket.on('user_joined', (updatedPopup) => {
      setPopup(updatedPopup);
      toast.success('A user joined the popup');
    });

    socket.on('user_left', (updatedPopup) => {
      setPopup(updatedPopup);
      toast.info('A user left the popup');
    });

    socket.on('branch_removed', (data) => {
      setPopup(data.popup);
      if (data.removed_branch_id === user.id) {
        toast.error('You have been removed from this popup');
        navigate('/dashboard');
      }
    });

    socket.on('personal_mode_toggled', (updatedPopup) => {
      setPopup(updatedPopup);
      toast.info(`Personal mode ${updatedPopup.is_personal ? 'enabled' : 'disabled'}`);
    });

    socket.on('popup_closed', (data) => {
      toast.info(`Popup closed${data.saved ? ' and saved' : ''}`);
      setTimeout(() => navigate('/dashboard'), 2000);
    });

    // Live session events
    socket.on('live_session_started', (data) => {
      setLiveSession(data);
      if (data.admin_id !== user.id) {
        toast.success(`Live session started! Mode: ${data.mode === 'broadcast' ? 'Broadcast' : 'Conference'}`);
      }
    });

    socket.on('live_session_ended', () => {
      setLiveSession(null);
      setIsInLiveSession(false);
      toast.info('Live session ended');
    });

    // Game invite from another user
    socket.on('game_invite', (data) => {
      // Always track how many players are playing
      setActiveGamePlayers(data.player_count || 1);
      // Show invite banner only if we're not already in the game
      if (!showGame) {
        setGameInvite(data);
      }
    });

    // Fetch popup and messages
    fetchPopup();
    fetchMessages();

    return () => {
      if (socket) {
        socket.emit('leave_room', { popup_id: popupId });
        socket.off('game_invite');
        socket.disconnect();
      }
    };
  }, [popupId, isHistory]);

  useEffect(() => {
    if (popup) {
      setIsAdmin(popup.admin_id === user.id);

      // Join popup if not admin AND haven't joined yet
      if (popup.admin_id !== user.id && !isHistory && !hasJoined.current) {
        hasJoined.current = true;
        socket.emit('join_popup', {
          popup_id: popupId,
          user: {
            id: user.id,
            username: user.username,
            profile_pic: user.profile_pic
          }
        });
      }
    }
  }, [popup, user.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchPopup = async () => {
    try {
      const response = await axios.get(`${API}/popup/${popupId}`);
      setPopup(response.data);
    } catch (error) {
      console.error('Error fetching popup:', error);
      toast.error('Failed to load popup');
      navigate('/dashboard');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/${popupId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = () => {
    if (!messageInput.trim() || isHistory) return;

    socket.emit('send_message', {
      popup_id: popupId,
      user_id: user.id,
      username: user.username,
      content: messageInput.trim(),
      message_type: 'text'
    });

    setMessageInput('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || isHistory) return;

    // Reset file input for re-upload of same file
    e.target.value = '';

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only images allowed (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image too large. Max size: 5MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('chatAppToken');
      const formData = new FormData();
      formData.append('file', file);

      // Upload to Cloudinary via backend
      const response = await axios.post(`${API}/chat/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        // Send message with Cloudinary URL
        socket.emit('send_message', {
          popup_id: popupId,
          user_id: user.id,
          username: user.username,
          content: file.name,
          message_type: 'file',
          file_data: response.data.url,
          file_name: file.name
        });
        toast.success('Image shared!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleEmojiSelect = (emojiData) => {
    setMessageInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleRemoveBranch = (branchId) => {
    socket.emit('remove_branch', {
      popup_id: popupId,
      branch_id: branchId,
      admin_id: user.id
    });
  };

  const handleTogglePersonalMode = () => {
    socket.emit('toggle_personal_mode', {
      popup_id: popupId,
      is_personal: !popup.is_personal,
      admin_id: user.id
    });
  };

  const handleLeavePopup = () => {
    socket.emit('leave_popup', {
      popup_id: popupId,
      user_id: user.id
    });
    navigate('/dashboard');
  };

  const handleClosePopup = () => {
    socket.emit('close_popup', {
      popup_id: popupId,
      admin_id: user.id,
      save_session: saveSession
    });
    setShowCloseDialog(false);
  };

  // Message edit/delete handlers
  const handleStartEdit = (message) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
  };

  const handleSaveEdit = async (messageId) => {
    if (!editContent.trim()) return;

    try {
      const token = localStorage.getItem('chatAppToken');
      await axios.patch(`${API}/messages/${messageId}`, {
        content: editContent.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local messages
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, content: editContent.trim(), is_edited: true } : m
      ));

      // Emit socket event for real-time update
      socket.emit('message_edited', {
        popup_id: popupId,
        message_id: messageId,
        content: editContent.trim()
      });

      setEditingMessageId(null);
      setEditContent('');
      toast.success('Message updated');
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to edit message');
    }
  };

  const handleDeleteMessage = async () => {
    if (!deleteMessageDialog.message) return;

    try {
      const token = localStorage.getItem('chatAppToken');
      await axios.delete(`${API}/messages/${deleteMessageDialog.message.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove from local messages
      setMessages(prev => prev.filter(m => m.id !== deleteMessageDialog.message.id));

      // Emit socket event
      socket.emit('message_deleted', {
        popup_id: popupId,
        message_id: deleteMessageDialog.message.id
      });

      setDeleteMessageDialog({ open: false, message: null });
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  const handleReportMessage = (message) => {
    setReportModal({
      open: true,
      message: message
    });
  };

  const handleStartLive = (mode) => {
    socket.emit('start_live_session', {
      popup_id: popupId,
      admin_id: user.id,
      mode: mode
    });
    setLiveSession({ mode, admin_id: user.id, popup_id: popupId });
    setIsInLiveSession(true);
    toast.success(`Starting ${mode === 'broadcast' ? 'Broadcast' : 'Conference'}...`);
  };

  const handleEndLive = (s3Key = null) => {
    socket.emit('end_live_session', {
      popup_id: popupId,
      admin_id: user.id
    });
    setLiveSession(null);
    setIsInLiveSession(false);

    // If there's a recording S3 key, show save dialog
    if (s3Key && isAdmin) {
      setPendingS3Key(s3Key);
      setShowSaveRecordingDialog(true);
    }
  };

  const handleSaveRecording = async (saveToChat) => {
    if (!pendingS3Key) {
      setShowSaveRecordingDialog(false);
      return;
    }

    if (!saveToChat) {
      // User chose to discard - just close dialog
      setShowSaveRecordingDialog(false);
      setPendingS3Key(null);
      toast.info('Recording discarded');
      return;
    }

    setIsSavingRecording(true);
    try {
      // Call backend to transfer from S3 to Cloudinary and save to chat
      const response = await axios.post(`${API}/recording/save-to-chat`, {
        popup_id: popupId,
        s3_key: pendingS3Key,
        save_to_chat: true
      });

      if (response.data.success) {
        toast.success('Recording saved to chat!');
      }
    } catch (error) {
      console.error('Error saving recording:', error);
      toast.error('Failed to save recording');
    } finally {
      setIsSavingRecording(false);
      setShowSaveRecordingDialog(false);
      setPendingS3Key(null);
    }
  };

  const handleJoinLive = () => {
    setIsInLiveSession(true);
  };

  if (!popup) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#2e0510' }}>
      {/* Header */}
      <div className="shadow-md p-4" style={{
        backgroundColor: '#311214',
        borderBottom: '2px solid #260e10',
        /* boxShadow: '0 4px 30px rgba(0, 0, 0, 0.7), 0 0 20px rgba(255, 200, 87, 0.2)'*/
      }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#FFC857', fontFamily: 'Space Grotesk, sans-serif' }}>
                {popup.name}
                {popup.is_personal && (
                  <Badge variant="secondary" style={{ backgroundColor: '#FFC857', color: '#1E1E1E' }} data-testid="personal-mode-badge">
                    <Lock className="w-3 h-3 mr-1" />
                    Personal
                  </Badge>
                )}
                {isHistory && (
                  <Badge variant="secondary" style={{ backgroundColor: '#47030F', color: '#FAFAFA' }}>
                    History
                  </Badge>
                )}
              </h1>
              <p className="text-sm" style={{ color: '#F5E3D7' }}>Admin: {popup.admin_username}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isHistory && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowGame(!showGame); if (!showGame) setGameInvite(null); }}
                style={{
                  borderColor: showGame ? '#e74c3c' : activeGamePlayers > 0 ? '#76ff03' : '#FFC857',
                  color: showGame ? '#e74c3c' : activeGamePlayers > 0 ? '#76ff03' : '#FFC857',
                  backgroundColor: showGame ? 'rgba(231,76,60,0.1)' : activeGamePlayers > 0 ? 'rgba(118,255,3,0.1)' : 'transparent',
                  position: 'relative'
                }}
                className="hover:bg-opacity-10 transition-all duration-300"
                title={activeGamePlayers > 0 ? `BlackBox Game (${activeGamePlayers} playing)` : 'BlackBox Game'}
              >
                <Gamepad2 className="w-4 h-4" />
                {activeGamePlayers > 0 && !showGame && (
                  <span style={{
                    position: 'absolute', top: -6, right: -6,
                    background: '#76ff03', color: '#000',
                    fontSize: 9, fontWeight: 'bold',
                    width: 16, height: 16, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 0 6px rgba(118,255,3,0.6)',
                    fontFamily: 'monospace'
                  }}>
                    {activeGamePlayers}
                  </span>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBranchList(true)}
              style={{
                borderColor: '#FFC857',
                color: '#FFC857',
                backgroundColor: 'transparent'
              }}
              className="hover:bg-opacity-10 transition-all duration-300"
              data-testid="show-branches-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              {popup.branches.length + 1} Users
            </Button>

            {!isHistory && (
              <>
                {isAdmin ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTogglePersonalMode}
                      style={{
                        borderColor: '#FFC857',
                        color: '#FFC857',
                        backgroundColor: 'transparent'
                      }}
                      className="hover:bg-opacity-10 transition-all duration-300"
                      data-testid="toggle-personal-mode-btn"
                    >
                      {popup.is_personal ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    </Button>
                    {!liveSession ? (
                      <Button
                        size="sm"
                        onClick={() => setShowLiveModeSelector(true)}
                        style={{
                          background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                          color: '#FAFAFA',
                          boxShadow: '0 2px 10px rgba(231, 76, 60, 0.4)'
                        }}
                        className="transition-all duration-300 hover:scale-105"
                        data-testid="go-live-btn"
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Go Live
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={handleEndLive}
                        style={{
                          background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                          color: '#FAFAFA'
                        }}
                        className="animate-pulse"
                      >
                        <Radio className="w-4 h-4 mr-2" />
                        Live Now
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowCloseDialog(true)}
                      style={{
                        background: '#f3b15b',
                        color: '#2e0510',
                        boxShadow: '0 2px 10px rgba(231, 114, 46, 0.4)'
                      }}
                      className="transition-all duration-300 hover:scale-105"
                      data-testid="close-popup-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Close Popup
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLeavePopup}
                    style={{
                      borderColor: '#FFC857',
                      color: '#FFC857',
                      backgroundColor: 'transparent'
                    }}
                    className="hover:bg-opacity-10 transition-all duration-300"
                    data-testid="leave-popup-btn"
                  >
                    Leave
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* BlackBox Game Overlay — floats on TOP of everything */}
      {showGame && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 50,
          backgroundColor: '#000',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            position: 'absolute', top: 10, right: 10, zIndex: 51
          }}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGame(false)}
              style={{
                borderColor: '#e74c3c',
                color: '#e74c3c',
                backgroundColor: 'rgba(0,0,0,0.7)',
                fontSize: '12px'
              }}
            >
              ✕ Exit Game (ESC)
            </Button>
          </div>
          <BlackBoxGame
            socket={socket}
            popupId={popupId}
            user={user}
            onExit={() => setShowGame(false)}
          />
        </div>
      )}

      {/* Game Invite Banner — shown when another user invites to play */}
      {gameInvite && !showGame && (
        <div style={{
          position: 'fixed',
          bottom: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 60,
          background: 'rgba(0,0,0,0.9)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 12,
          padding: '12px 20px',
          display: 'flex', alignItems: 'center', gap: 16,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Gamepad2 style={{ color: '#FFC857', width: 20, height: 20 }} />
            <span style={{ color: '#fff', fontSize: 13, fontFamily: 'monospace' }}>
              <strong>{gameInvite.inviter}</strong> is playing BlackBox
              {gameInvite.player_count > 1 ? ` (${gameInvite.player_count} players)` : ''}
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => { setShowGame(true); setGameInvite(null); }}
            style={{
              background: '#FFC857', color: '#000', border: 'none',
              fontFamily: 'monospace', fontWeight: 'bold', fontSize: 11,
              letterSpacing: 1
            }}
          >
            JOIN GAME
          </Button>
          <button
            onClick={() => setGameInvite(null)}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
              cursor: 'pointer', fontSize: 16, padding: '0 4px'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Main Content Area - Side by Side ONLY when Live */}
      {(liveSession && isInLiveSession ? (
        /* Split Layout when Live */
        <div className="flex-1 flex flex-row max-w-full w-full mx-auto px-4 py-4 gap-4 overflow-hidden">
          {/* Live Video Panel - appears on the left when live */}
          <div className="w-3/5 h-full rounded-xl overflow-hidden" style={{ minHeight: '500px' }}>
            {liveSession.mode === 'broadcast' ? (
              <BroadcastView
                socket={socket}
                popupId={popupId}
                user={user}
                isAdmin={isAdmin}
                liveSession={liveSession}
                onEndSession={handleEndLive}
              />
            ) : (
              <ConferenceView
                socket={socket}
                popupId={popupId}
                user={user}
                isAdmin={isAdmin}
                liveSession={liveSession}
                onEndSession={handleEndLive}
              />
            )}
          </div>

          {/* Chat Area when Live - 40% width */}
          <div className="w-2/5 flex flex-col">
            <ScrollArea className="flex-1 pr-4" data-testid="messages-area" style={{ maxHeight: 'calc(100vh - 250px)' }}>
              <div className="space-y-6">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 animate-slide-in ${message.user_id === user.id ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${message.id}`}
                  >
                    {message.user_id !== user.id && (
                      <Avatar className="w-12 h-12 flex-shrink-0 mt-1" style={{ border: '2px solid #47030F' }}>
                        <AvatarFallback style={{ background: 'linear-gradient(135deg, #e7722eff 0%, #47030F 100%)', color: '#F5E3D7' }} className="text-sm font-bold">
                          {message.username.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className={`flex flex-col max-w-lg ${message.user_id === user.id ? 'items-end' : 'items-start'}`}>
                      {/* Username badge for other users */}
                      {message.user_id !== user.id && (
                        <span
                          className="text-xs font-semibold mb-1 ml-2"
                          style={{ color: getUserColor(message.user_id) }}
                        >
                          {message.username}
                        </span>
                      )}
                      <div
                        className="px-5 py-3 rounded-3xl shadow-lg"
                        style={{
                          background: message.user_id === user.id
                            ? 'linear-gradient(135deg, #c17342 0%, #b8693d 100%)'
                            : '#f5ddcb',
                          color: message.user_id === user.id ? '#FAFAFA' : '#1E1E1E',
                          borderRadius: message.user_id === user.id ? '24px 24px 4px 24px' : '24px 24px 24px 4px',
                          boxShadow: message.user_id === user.id
                            ? '0 4px 12px rgba(193, 115, 66, 0.4)'
                            : '0 2px 8px rgba(0, 0, 0, 0.15)',
                          minWidth: '80px'
                        }}
                      >
                        {message.message_type === 'video' ? (
                          <div className="space-y-2">
                            <div className="relative rounded-2xl overflow-hidden" style={{ maxWidth: '320px' }}>
                              <video
                                src={message.file_data}
                                controls
                                className="w-full h-auto"
                                style={{ maxHeight: '240px' }}
                              />
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Video className="w-4 h-4" />
                              <span>{message.file_name || 'Live Recording'}</span>
                            </div>
                          </div>
                        ) : message.message_type === 'file' ? (
                          <div className="space-y-2">
                            {message.file_data && (message.file_data.startsWith('data:image/') ||
                              message.file_name?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) ? (
                              <div className="space-y-2">
                                <img
                                  src={message.file_data}
                                  alt={message.file_name || 'Shared image'}
                                  className="rounded-2xl max-w-full h-auto max-h-80 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(message.file_data, '_blank')}
                                />
                                <div className="flex items-center gap-2 text-sm">
                                  <Paperclip className="w-4 h-4" />
                                  <span>{message.file_name || message.content}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Paperclip className="w-4 h-4" />
                                <span>{message.content}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap break-words text-base leading-relaxed">{message.content}</p>
                        )}
                      </div>
                      <span
                        className="text-xs mt-1 px-2"
                        style={{
                          color: '#F5E3D7',
                          opacity: 0.7,
                          fontSize: '11px'
                        }}
                      >
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Message Input */}
            {!isHistory && (
              <div className="mt-6 mx-6 mb-6 rounded-full shadow-2xl p-2 flex items-center gap-3" style={{
                backgroundColor: 'rgba(30, 30, 30, 0.95)',
                border: '1px solid rgba(255, 200, 87, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)'
              }}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    color: '#F5E3D7',
                    backgroundColor: 'transparent'
                  }}
                  className="transition-all duration-300 hover:bg-opacity-10 rounded-full"
                  data-testid="attach-file-btn"
                >
                  <Paperclip className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  style={{
                    color: '#F5E3D7',
                    backgroundColor: 'transparent'
                  }}
                  className="transition-all duration-300 hover:bg-opacity-10 rounded-full"
                  data-testid="emoji-picker-btn"
                >
                  <Smile className="w-5 h-5" />
                </Button>
                <Input
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1 h-12 border-0 bg-transparent text-base transition-all duration-300 focus-visible:ring-0 focus-visible:ring-offset-0"
                  style={{
                    color: '#F5E3D7',
                    outline: 'none'
                  }}
                  data-testid="message-input"
                />
                <Button
                  onClick={handleSendMessage}
                  size="icon"
                  className="font-bold transition-all duration-300 hover:scale-110 rounded-full w-12 h-12"
                  style={{
                    background: 'linear-gradient(135deg, #c17342 0%, #b8693d 100%)',
                    color: '#FAFAFA',
                    boxShadow: '0 4px 15px rgba(193, 115, 66, 0.5)'
                  }}
                  data-testid="send-message-btn"
                >
                  <Send className="w-5 h-5" />
                </Button>
                {showEmojiPicker && (
                  <div className="absolute bottom-20 right-8 z-50">
                    <EmojiPicker onEmojiClick={handleEmojiSelect} />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Original Wide Layout when NOT Live */
        <div className="flex-1 max-w-5xl w-full mx-auto px-6 py-8 flex flex-col">
          <ScrollArea className="flex-1 pr-4" data-testid="messages-area">
            <div className="space-y-6">
              {messages.map((message) => (
                // System messages (like resume dividers) get special styling
                message.message_type === 'system' ? (
                  <div key={message.id} className="flex justify-center my-4">
                    <div
                      className="px-4 py-2 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: 'rgba(78, 205, 196, 0.2)',
                        color: '#4ECDC4',
                        border: '1px solid rgba(78, 205, 196, 0.3)'
                      }}
                    >
                      {message.content}
                    </div>
                  </div>
                ) : (
                  <div
                    key={message.id}
                    className={`flex gap-3 animate-slide-in group ${message.user_id === user.id ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${message.id}`}
                    onMouseEnter={() => setHoveredMessageId(message.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    {message.user_id !== user.id && (
                      <Avatar className="w-12 h-12 flex-shrink-0 mt-1" style={{ border: '2px solid #47030F' }}>
                        <AvatarFallback style={{ background: 'linear-gradient(135deg, #e7722eff 0%, #47030F 100%)', color: '#F5E3D7' }} className="text-sm font-bold">
                          {message.username.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    )}

                    {/* Action buttons for own messages - left side */}
                    {message.user_id === user.id && hoveredMessageId === message.id && editingMessageId !== message.id && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleStartEdit(message)}
                          className="p-1.5 rounded-full hover:bg-[#3d1a24] transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-[#F5E3D7]/60" />
                        </button>
                        <button
                          onClick={() => setDeleteMessageDialog({ open: true, message })}
                          className="p-1.5 rounded-full hover:bg-red-600/20 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-400/60" />
                        </button>
                      </div>
                    )}

                    <div className={`flex flex-col max-w-lg ${message.user_id === user.id ? 'items-end' : 'items-start'}`}>
                      {message.user_id !== user.id && (
                        <span className="text-xs font-semibold mb-1 ml-2" style={{ color: getUserColor(message.user_id) }}>
                          {message.username}
                        </span>
                      )}

                      {/* Message bubble with edit mode */}
                      {editingMessageId === message.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(message.id)}
                            className="min-w-[200px] border-0"
                            style={{ backgroundColor: '#260e10', color: '#F5E3D7' }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveEdit(message.id)}
                            className="p-1.5 rounded-full bg-green-600 hover:bg-green-700 transition-colors"
                          >
                            <Check className="w-4 h-4 text-white" />
                          </button>
                          <button
                            onClick={() => { setEditingMessageId(null); setEditContent(''); }}
                            className="p-1.5 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ) : (
                        <div
                          className="px-5 py-3 rounded-3xl shadow-lg"
                          style={{
                            background: message.user_id === user.id ? 'linear-gradient(135deg, #c17342 0%, #b8693d 100%)' : '#f5ddcb',
                            color: message.user_id === user.id ? '#FAFAFA' : '#1E1E1E',
                            borderRadius: message.user_id === user.id ? '24px 24px 4px 24px' : '24px 24px 24px 4px',
                            boxShadow: message.user_id === user.id ? '0 4px 12px rgba(193, 115, 66, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.15)',
                            minWidth: '80px'
                          }}
                        >
                          {message.message_type === 'video' ? (
                            <div className="space-y-2">
                              <div className="relative rounded-2xl overflow-hidden" style={{ maxWidth: '320px' }}>
                                <video src={message.file_data} controls className="w-full h-auto" style={{ maxHeight: '240px' }} />
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Video className="w-4 h-4" />
                                <span>{message.file_name || 'Live Recording'}</span>
                              </div>
                            </div>
                          ) : message.message_type === 'file' ? (
                            <div className="space-y-2">
                              {message.file_data && (message.file_data.startsWith('data:image/') || message.file_name?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) ? (
                                <div className="space-y-2">
                                  <img src={message.file_data} alt={message.file_name || 'Shared image'} className="rounded-2xl max-w-full h-auto max-h-80 object-contain cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(message.file_data, '_blank')} />
                                  <div className="flex items-center gap-2 text-sm">
                                    <Paperclip className="w-4 h-4" />
                                    <span>{message.file_name || message.content}</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Paperclip className="w-4 h-4" />
                                  <span>{message.content}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="whitespace-pre-wrap break-words text-base leading-relaxed">
                              {message.content}
                              {message.is_edited && <span className="text-xs opacity-60 ml-2">(edited)</span>}
                            </p>
                          )}
                        </div>
                      )}

                      <span className="text-xs mt-1 px-2" style={{ color: '#F5E3D7', opacity: 0.7, fontSize: '11px' }}>
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    {/* Report button for other users' messages - right side */}
                    {message.user_id !== user.id && hoveredMessageId === message.id && (
                      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleReportMessage(message)}
                          className="p-1.5 rounded-full hover:bg-red-600/20 transition-colors"
                          title="Report"
                        >
                          <Flag className="w-4 h-4 text-red-400/60" />
                        </button>
                      </div>
                    )}
                  </div>
                )))}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Message Input - Original Layout */}
          {!isHistory && (
            <div className="mt-6 mx-6 mb-6 rounded-full shadow-2xl p-2 flex items-center gap-3" style={{ backgroundColor: 'rgba(30, 30, 30, 0.95)', border: '1px solid rgba(255, 200, 87, 0.2)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)' }}>
              {/* Attach button with upload progress */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => !isUploading && fileInputRef.current?.click()}
                disabled={isUploading}
                style={{ color: isUploading ? '#FFC857' : '#F5E3D7', backgroundColor: 'transparent' }}
                className="transition-all duration-300 hover:bg-opacity-10 rounded-full relative"
                data-testid="attach-file-btn"
              >
                {isUploading ? (
                  <div className="relative w-5 h-5">
                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold">{uploadProgress}%</span>
                  </div>
                ) : (
                  <Paperclip className="w-5 h-5" />
                )}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setShowEmojiPicker(!showEmojiPicker)} style={{ color: '#F5E3D7', backgroundColor: 'transparent' }} className="transition-all duration-300 hover:bg-opacity-10 rounded-full" data-testid="emoji-picker-btn">
                <Smile className="w-5 h-5" />
              </Button>
              <Input placeholder="Type a message..." value={messageInput} onChange={(e) => setMessageInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()} className="flex-1 h-12 border-0 bg-transparent text-base transition-all duration-300 focus-visible:ring-0 focus-visible:ring-offset-0" style={{ color: '#F5E3D7', outline: 'none' }} data-testid="message-input" />
              <Button onClick={handleSendMessage} size="icon" className="font-bold transition-all duration-300 hover:scale-110 rounded-full w-12 h-12" style={{ background: 'linear-gradient(135deg, #c17342 0%, #b8693d 100%)', color: '#FAFAFA', boxShadow: '0 4px 15px rgba(193, 115, 66, 0.5)' }} data-testid="send-message-btn">
                <Send className="w-5 h-5" />
              </Button>
              {showEmojiPicker && (
                <div className="absolute bottom-20 right-8 z-50">
                  <EmojiPicker onEmojiClick={handleEmojiSelect} />
                </div>
              )}
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
            </div>
          )}
        </div>
      ))}

      {/* Branch List Dialog */}
      <Dialog open={showBranchList} onOpenChange={setShowBranchList}>
        <DialogContent style={{ backgroundColor: '#1E1E1E', borderColor: '#FFC857', border: '2px solid' }} data-testid="branches-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: '#FFC857' }}>Users in this Popup</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#47030F', border: '2px solid #FFC857' }}>
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarFallback style={{ background: 'linear-gradient(135deg, #FFC857 0%, #e7722eff 100%)', color: '#47030F' }}>
                    {popup.admin_username.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium" style={{ color: '#F5E3D7' }}>{popup.admin_username}</p>
                  <Badge variant="secondary" className="text-xs font-bold" style={{ backgroundColor: '#FFC857', color: '#47030F' }}>Admin</Badge>
                </div>
              </div>
            </div>
            {popup.branches.map((branch) => (
              <div key={branch.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#47030F', border: '1px solid rgba(255, 200, 87, 0.3)' }}>
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={branch.profile_pic} />
                    <AvatarFallback style={{ background: 'linear-gradient(135deg, #FFC857 0%, #e7722eff 100%)', color: '#47030F' }}>
                      {branch.username.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <p className="font-medium" style={{ color: '#F5E3D7' }}>{branch.username}</p>
                </div>
                {isAdmin && !isHistory && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRemoveBranch(branch.id)}
                    style={{ backgroundColor: '#e7722eff', color: '#FAFAFA' }}
                    data-testid={`remove-branch-btn-${branch.id}`}
                  >
                    <UserMinus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Popup Dialog */}
      <AlertDialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <AlertDialogContent style={{ backgroundColor: '#260e10', borderColor: '#f3b15b', border: '2px solid' }} data-testid="close-popup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#FFC857' }}>Close Popup</AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#F5E3D7' }}>
              Do you want to save this session before closing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 my-4">
            <input
              type="checkbox"
              checked={saveSession}
              onChange={(e) => setSaveSession(e.target.checked)}
              className="w-4 h-4"
              style={{ accentColor: '#FFC857' }}
              data-testid="save-session-checkbox"
            />
            <label className="text-sm" style={{ color: '#3112147' }}>Save this session to history</label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: '#FFC857', backgroundColor: '#311214', color: '#FFC857' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClosePopup}
              style={{ background: '#f3b15b', color: '#47030F', fontWeight: 'bold' }}
              data-testid="confirm-close-popup-btn"
            >
              Close Popup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Recording Dialog */}
      <AlertDialog open={showSaveRecordingDialog} onOpenChange={setShowSaveRecordingDialog}>
        <AlertDialogContent style={{ backgroundColor: '#1E1E1E', borderColor: '#FFC857', border: '2px solid' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#FFC857' }}>Save Recording?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#F5E3D7' }}>
              Your live session was recorded. Would you like to save it to the chat for others to view?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => handleSaveRecording(false)}
              disabled={isSavingRecording}
              style={{ borderColor: '#FFC857', backgroundColor: '#311214', color: '#FFC857' }}
            >
              {isSavingRecording ? 'Processing...' : 'Discard'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleSaveRecording(true)}
              disabled={isSavingRecording}
              style={{ background: '#f3b15b', color: '#47030F', fontWeight: 'bold' }}
            >
              {isSavingRecording ? 'Saving...' : 'Save to Chat'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Live Mode Selector Modal */}
      <LiveModeSelector
        isOpen={showLiveModeSelector}
        onClose={() => setShowLiveModeSelector(false)}
        onSelectMode={handleStartLive}
      />

      {/* Join Live Notification for non-admins */}
      {liveSession && !isInLiveSession && !isAdmin && (
        <div className="fixed bottom-4 right-4 z-40 animate-bounce">
          <Button
            onClick={handleJoinLive}
            style={{
              background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
              color: '#FAFAFA',
              boxShadow: '0 4px 20px rgba(231, 76, 60, 0.5)'
            }}
            className="px-6 py-3 text-lg"
          >
            <Video className="w-5 h-5 mr-2" />
            {liveSession.mode === 'broadcast' ? 'Watch Live' : 'Join Conference'}
          </Button>
        </div>
      )}

      {/* Delete Message Confirmation Dialog */}
      <AlertDialog open={deleteMessageDialog.open} onOpenChange={(open) => setDeleteMessageDialog({ open, message: null })}>
        <AlertDialogContent style={{ backgroundColor: '#1E1E1E', borderColor: '#FFC857', border: '2px solid' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#FFC857' }}>Delete Message?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#F5E3D7' }}>
              Are you sure you want to delete this message? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: '#FFC857', backgroundColor: '#311214', color: '#FFC857' }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMessage}
              style={{ background: '#e74c3c', color: '#FAFAFA', fontWeight: 'bold' }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModal.open}
        onClose={() => setReportModal({ open: false, message: null })}
        contentType="message"
        contentId={reportModal.message?.id}
        contentPreview={reportModal.message?.content}
        popupId={popupId}
        targetUserId={reportModal.message?.user_id}
        targetUsername={reportModal.message?.username}
      />
    </div>
  );
};

export default ChatRoom;